import React from "react";
import {TFunction} from "i18next";
import {TableDataChartBuilder} from "./TableDataChartBuilder";
import {services} from "../../application/service/services";
import {TooltipItem} from "chart.js";
import {VisualizationInstance} from "../../report/model/viz/VisualizationInstance";


export class PieChartBuilder extends TableDataChartBuilder {

    private dataIndexByGroup = {};
    private otherHidden = false;

    constructor(viz: VisualizationInstance, t: TFunction) {
        super('pie', viz, t, "dashboard.viz.distributionChart.group.other");
        return this;
    }

    protected legendOnClickHandler = (event, legendItem, legend) => {
        //if (this.otherIndex.indexOf(legendItem.datasetIndex) >= 0) return;
        const ci = legend.chart;
        const index = legendItem.index;
        if (index == this.otherIndex[0]) {
            this.otherHidden = !legendItem.hidden;
        }
        if (!legendItem.hidden) {
            this.hiddenItems.add(legendItem.text);
        } else {
            this.hiddenItems.delete(legendItem.text);
        }

        ci.data = this.computeDatasetsAndLabels();
        ci.toggleDataVisibility(index);
        ci.update();
    }

    _tooltipRenderer = (value: any, tooltipItem: TooltipItem<any>) => {
        const field = services.getFieldsService().getField(this.viz.definition.params.columns[0].field);
        const tdStyle = {borderTop: '4px solid  rgba(0,0,0,0.0)', borderLeft: '14px solid  rgba(0,0,0,0.0)'};
        return <table style={{textAlign: 'left', margin: '0 auto', borderCollapse: 'collapse', borderStyle: 'hidden'}}>
            <thead>
            <tr>
                {field ?
                    <th style={tdStyle}>{this.t('dashboard.viz.pieChart.tooltip.field')}</th> : null}
                <th style={tdStyle}>{this.t('dashboard.viz.pieChart.tooltip.value')}</th>
                <th style={tdStyle}>{this.t('dashboard.viz.pieChart.tooltip.count')}</th>
            </tr>
            </thead>
            <tbody>
            <tr>
                {field ? <td style={tdStyle}>{field.getLabel(this.t)}</td> : null}
                <td style={tdStyle}>{value.label}</td>
                <td style={tdStyle}>{value.value + '(' + value.percentage?.toFixed(2) + ')'}</td>
            </tr>
            </tbody>
        </table>;
    };

    protected prepareChartObject(chartObject) {
        const chartOptions: any = chartObject.options;
        chartOptions.parsing = {key: 'percentage'};
        if (chartOptions.plugins.legend && chartOptions.plugins.legend.display) {
            chartOptions.plugins.legend.onClick = this.legendOnClickHandler;
            chartOptions.plugins.legend.labels = {
                font: {
                    size: 14,
                    family: this.getFont()
                }
            };
        }

        if (this._displayLabelsInline) {
            chartOptions.plugins.datalabels = {
                ...chartOptions.plugins.datalabels,
                ...{
                    anchor: 'end',
                    offset: 10,
                    align: 'start',
                    color: 'white',
                    formatter: value => (value.percentage ? Math.round(value.percentage) : '') + "﹪",
                }
            }
        }
        return chartObject;
    }

    protected computeDatasetsAndLabels() {
        const formattedData = {
            labels: [],
            datasets: [{
                data: [],
                backgroundColor: []
            }]
        };
        let index = 0;
        const data = [];
        const otherCount = this.computeOther();
        const otherLabel = this.getLabelWithCount(this.getOtherGroupName(), otherCount);
        const displayedTotalCount = this.data.items
            .filter(entry => !this.hiddenItems.has(this.getLabelWithCount(this.getFieldValueLabel(entry), this.getVolume(entry).count)))
            .reduce((total, entry) => total + this.getVolume(entry).count, 0);

        this.data.items
            .forEach((entry) => {
                const key = this.getFieldValueLabel(entry);
                const volume = this.getVolume(entry);
                const labelWithCount = this.getLabelWithCount(key, volume.count);
                let percent = volume.percent;
                if (this.otherHidden) {
                    //compute percent on displayedItem only
                    percent = volume.count * 100 / displayedTotalCount;
                }
                data.push({item: entry, value: volume.count, percentage: percent});
                this.dataIndexByGroup[key] = index;
                formattedData.labels.push(labelWithCount);
                formattedData.datasets[0].backgroundColor.push(services.getColorService().getColorForFieldValue(entry.field, entry.fieldValue));
                index++;
            });

        this.otherIndex = [data.length];
        this.dataIndexByGroup[this.otherKey] = index;
        if (otherCount !== 0) {
            data.push({
                item: {field: this.viz.definition.params.field, fieldValue: 'other'},
                group: this.getOtherGroupName(),
                label: this.getOtherGroupName(),
                value: otherCount,
                percentage: otherCount * 100 / this.data.totalCount
            });
            formattedData.labels.push(otherLabel);
        }
        formattedData.datasets[0].data = data;
        formattedData.datasets[0].backgroundColor.push(services.getColorService().getOtherColor());
        return formattedData;
    }


    computeOther(): number {
        let otherCount = this.data.totalCount;
        this.data.items
            .filter(entry => {
                const key = this.getFieldValueLabel(entry);
                return !this.hiddenItems.has(this.getLabelWithCount(key, this.getVolume(entry).count))
            })
            .forEach((entry) => {
                const volume = this.getVolume(entry);
                otherCount -= volume.count;
            });
        return otherCount;
    }

    getDataToExport(): any[][] {
        const dataToExport = [];
        //Add Header
        const header = [];

        const field = services.getFieldsService().getField(this.viz.definition.params.columns[0].field);
        header.push(field ? field.getLabel(this.t) : this.viz.definition.params.columns[0].field);
        header.push(this.t('dashboard.viz.pieChart.tooltip.count'));
        header.push(this.t('dashboard.viz.pieChart.tooltip.percentage'));
        dataToExport.push(header);

        //Add value
        this.formattedData.datasets[0].data
            .filter(value => !this.hiddenItems.has(value.label) || value.group === this.getOtherGroupName())
            .forEach(value => {
                const lineToExport = [];
                lineToExport.push(value.label);
                lineToExport.push(value.value);
                lineToExport.push(value.percentage?.toFixed(2));
                dataToExport.push(lineToExport);
            })
        return dataToExport;
    }
}